import { Observable } from 'rxjs';
import type { CambioPrevisto, OperacionPrevisible } from '@schemas/cambios';
import type {
  Aplicacion,
  AplicacionInput,
  Funcion,
  FuncionInput,
  Modulo,
  ModuloInput,
  Rol,
  RolInput,
} from '@schemas/accesos';

/**
 * Contrato del gestor de accesos.
 *
 * Es una clase ABSTRACTA y no una interfaz, por el mismo motivo que
 * `AuthService`: una interfaz desaparece al compilar y no sirve como token de
 * inyección; una abstracta se inyecta directa y el compilador verifica que la
 * implementación la cumpla entera.
 *
 * La única implementación es `AccesosHttpService`, contra `AdminPortal.API`
 * sobre el esquema `acceso` de la base propia.
 *
 * Hubo una segunda —`AccesosMockService`, sobre `localStorage`— mientras el
 * esquema de la base se reestructuraba: cablear pantallas contra tablas
 * condenadas era trabajo que habría que rehacer. Ya se retiró, y que esto siga
 * siendo una clase abstracta con una sola implementación NO es un resto de
 * aquello: es lo que permite cambiar de implementación tocando una línea de
 * `app.config.ts`, y lo que hizo que sustituir el mock por HTTP no tocara una
 * sola pantalla.
 *
 * ⚠️ Todos los métodos devuelven `Observable`, y eso venía de cuando el mock
 * resolvía en memoria. Devolver valores sincrónicos habría ahorrado un
 * `subscribe` entonces y obligado a reescribir cada pantalla el día de la
 * conexión — que es exactamente cuando menos se quiere tocar la interfaz.
 */
export abstract class AccesosService {
  // ── Aplicaciones ───────────────────────────────────────────────────────────

  /** Todas las aplicaciones, con sus conteos de módulos y roles. */
  abstract listarAplicaciones(): Observable<readonly Aplicacion[]>;

  abstract crearAplicacion(input: AplicacionInput, motivo: string): Observable<Aplicacion>;

  abstract actualizarAplicacion(
    id: number,
    input: AplicacionInput,
    motivo: string,
  ): Observable<Aplicacion>;

  /**
   * Baja LÓGICA. Falla con `conflict` si tiene roles enlazados: el rol apunta a
   * la URL de la aplicación, así que apagarla dejaría a sus usuarios entrando a
   * un destino que ya no existe.
   */
  abstract desactivarAplicacion(id: number, motivo: string): Observable<void>;

  abstract reactivarAplicacion(id: number, motivo: string): Observable<Aplicacion>;

  // ── Módulos ────────────────────────────────────────────────────────────────

  /** Módulos de una aplicación, ordenados por `orden`. */
  abstract listarModulos(aplicacionId: number): Observable<readonly Modulo[]>;

  abstract crearModulo(input: ModuloInput, motivo: string): Observable<Modulo>;

  /**
   * Actualización PARCIAL: lo que no venga se conserva.
   *
   * **El `orden` NO se manda por aquí** — se rechaza. Mover un módulo es
   * intercambiarlo con su vecino (`moverModulo`), y aceptar un número suelto
   * permitiría dejar dos módulos con el mismo orden, o un hueco, y la pantalla
   * no tiene forma de reparar eso.
   */
  abstract actualizarModulo(
    id: number,
    input: Partial<ModuloInput>,
    motivo: string,
  ): Observable<Modulo>;

  /**
   * Mueve un módulo una posición en el menú, intercambiándolo con su vecino.
   *
   * Devuelve la lista COMPLETA de la aplicación y no el módulo movido, porque el
   * movimiento cambia DOS filas: con una sola, la pantalla tendría que recargar
   * para ver el efecto.
   *
   * Es una operación propia y no un `actualizarModulo({ orden })` porque el
   * intercambio tiene que ser atómico. Renumerar desde el cliente —leer la
   * lista, calcular los números nuevos y mandar una escritura por fila— deja la
   * lista a medio mover si una de esas escrituras falla.
   */
  abstract moverModulo(
    id: number,
    direccion: 'arriba' | 'abajo',
    motivo: string,
  ): Observable<readonly Modulo[]>;

  /**
   * Baja lógica. NO borra los enlaces a roles: reactivarlo devuelve el acceso
   * tal como estaba, que es lo que un operador espera de un interruptor.
   */
  abstract desactivarModulo(id: number, motivo: string): Observable<void>;

  abstract reactivarModulo(id: number, motivo: string): Observable<Modulo>;

  // ── Roles ──────────────────────────────────────────────────────────────────

  abstract listarRoles(): Observable<readonly Rol[]>;

  abstract crearRol(input: RolInput, motivo: string): Observable<Rol>;

  abstract actualizarRol(id: number, input: RolInput, motivo: string): Observable<Rol>;

  /** Falla si es el último superadministrador activo, o si tiene usuarios. */
  abstract desactivarRol(id: number, motivo: string): Observable<void>;

  abstract reactivarRol(id: number, motivo: string): Observable<Rol>;

  // ── Funciones ──────────────────────────────────────────────────────────────
  //
  // Una función es una parte ESPECÍFICA DENTRO de un módulo —un botón de la
  // columna de acciones, una sección de la página— y se OTORGA POR ROL, igual
  // que un módulo. Su `activo` es su ciclo de vida (existe o se retiró), NO un
  // permiso: el permiso viaja en `RolInput.funcionesCodigos`.
  //
  // Aquí se DECLARAN y se retiran; se otorgan al guardar un rol.

  /** Funciones de una aplicación, agrupables por su módulo. */
  abstract listarFunciones(aplicacionId: number): Observable<readonly Funcion[]>;

  /**
   * Funciones de un módulo. Es la que alimenta el selector del diálogo de rol,
   * que las muestra anidadas bajo cada módulo otorgado.
   */
  abstract listarFuncionesPorModulo(moduloId: number): Observable<readonly Funcion[]>;

  /**
   * Declara una función. El `codigo` lo compone el servidor a partir del módulo
   * y del nombre.
   */
  abstract crearFuncion(input: FuncionInput, motivo: string): Observable<Funcion>;

  /** Edición PARCIAL. El módulo, el padre y el código no cambian. */
  abstract actualizarFuncion(
    id: number,
    input: Partial<FuncionInput>,
    motivo: string,
  ): Observable<Funcion>;

  /**
   * Retira una función. Devuelve la lista COMPLETA de su módulo, no la función,
   * porque retirar una padre deja a sus hijas no disponibles: con una sola fila
   * la pantalla tendría que recargar para ver el efecto.
   *
   * Los otorgamientos NO se borran: reactivarla devuelve el acceso tal como
   * estaba.
   */
  abstract desactivarFuncion(id: number, motivo: string): Observable<readonly Funcion[]>;

  /** Reactivación. Falla si su función principal sigue retirada. */
  abstract reactivarFuncion(id: number, motivo: string): Observable<readonly Funcion[]>;

  /**
   * ENSAYA una operación y devuelve lo que cambiaría, sin guardar nada.
   *
   * Lo resuelve el SERVIDOR ejecutándola de verdad y deshaciéndola, no un diff
   * local: el cliente no puede saber si su copia de la fila está vieja, qué
   * columnas derivadas y filas en cascada arrastra la operación, ni si alguna
   * validación la va a rechazar. Un ensayo que falla devuelve **el mismo error**
   * que devolvería al confirmar.
   */
  abstract previsualizarAplicacion(
    operacion: OperacionPrevisible,
    id?: number,
    datos?: AplicacionInput,
  ): Observable<readonly CambioPrevisto[]>;

  abstract previsualizarModulo(
    operacion: OperacionPrevisible,
    id?: number,
    datos?: Partial<ModuloInput>,
  ): Observable<readonly CambioPrevisto[]>;

  abstract previsualizarRol(
    operacion: OperacionPrevisible,
    id?: number,
    datos?: RolInput,
  ): Observable<readonly CambioPrevisto[]>;

  abstract previsualizarFuncion(
    operacion: OperacionPrevisible,
    id?: number,
    datos?: Partial<FuncionInput>,
  ): Observable<readonly CambioPrevisto[]>;
}
